import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowLeft } from 'lucide-react';
import { attractions } from '../data/mock';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const ATTRACTION_SEO = {
  'Har-Ki-Pauri,Haridwar': {
    title: 'Har Ki Pauri Haridwar | Ganga Aarti & History',
    description: 'Explore the spiritual significance of Har Ki Pauri and Ganga Aarti in Haridwar.',
  },
  'har-ki-pauri': {
    title: 'Har Ki Pauri Haridwar | Ganga Aarti & History',
    description: 'Explore the spiritual significance of Har Ki Pauri and Ganga Aarti in Haridwar.',
  },
  'mansa-devi-temple': {
    title: 'Mansa Devi Temple Haridwar | Ropeway & Darshan Guide',
    description: 'Visit Mansa Devi Temple in Haridwar for blessings and panoramic views.',
  },
  'chandi-devi-temple': {
    title: 'Chandi Devi Temple Haridwar | Hilltop Shrine Guide',
    description: 'Discover Chandi Devi Temple in Haridwar and its religious importance.',
  },
  'rajaji-national-park': {
    title: 'Rajaji National Park Near Haridwar | Safari Guide',
    description: 'Experience wildlife safaris and nature at Rajaji National Park near Haridwar.',
  },
};

const AttractionDetail = () => {
  const { slug } = useParams();
  const attraction = attractions.find(a => a.slug === slug || a.name.toLowerCase().replace(/\s+/g, '-') === slug);

  if (!attraction) {
    return (
      <div className="min-h-screen pt-32 pb-20">
        <SEO title="Page Not Found | Hotel Suvidha Deluxe Haridwar" description="The page you are looking for could not be found." />
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-navy-900 mb-4">Attraction Not Found</h1>
          <p className="text-gray-600 mb-8">The attraction you're looking for doesn't exist.</p>
          <Link to="/about">
            <Button className="bg-gold-600 hover:bg-gold-700 text-white">
              Back to Attractions
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Parse markdown-like content to HTML
  const parseContent = (content) => {
    if (!content) return '';
    
    const lines = content.split('\n');
    let html = '';
    let inList = false;
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (!trimmed) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        return;
      }
      
      // H1
      if (trimmed.startsWith('# ') && !trimmed.startsWith('##')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h1 class="text-4xl font-bold text-navy-900 mb-6 font-serif mt-8">${trimmed.substring(2)}</h1>`;
        return;
      }
      
      // H2
      if (trimmed.startsWith('## ') && !trimmed.startsWith('###')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h2 class="text-3xl font-bold text-navy-900 mb-4 font-serif mt-8">${trimmed.substring(3)}</h2>`;
        return;
      }
      
      // H3
      if (trimmed.startsWith('### ')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h3 class="text-2xl font-bold text-navy-900 mb-3 mt-6">${trimmed.substring(4)}</h3>`;
        return;
      }
      
      // List items
      if (trimmed.startsWith('- ')) {
        if (!inList) {
          html += '<ul class="list-disc list-inside mb-4 space-y-2 text-gray-700">';
          inList = true;
        }
        html += `<li class="mb-2">${trimmed.substring(2)}</li>`;
        return;
      }
      
      // Close list if we hit a non-list item
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      
      // Bold text (standalone)
      if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4) {
        html += `<p class="text-lg font-bold text-navy-900 mb-4">${trimmed.substring(2, trimmed.length - 2)}</p>`;
        return;
      }
      
      // Process inline formatting
      let processedLine = trimmed
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-navy-900">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
      
      // Regular paragraph
      html += `<p class="text-gray-700 mb-4 leading-relaxed">${processedLine}</p>`;
    });
    
    if (inList) {
      html += '</ul>';
    }
    
    return html;
  };

  const htmlContent = parseContent(attraction.content);

  const seo = ATTRACTION_SEO[slug] || ATTRACTION_SEO[attraction.slug] || {
    title: `${attraction.name} | Hotel Suvidha Deluxe Haridwar`,
    description: attraction.description,
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      <SEO 
        title={seo.title}
        description={seo.description}
        keywords={`${attraction.name}, haridwar attractions, haridwar sightseeing, hotel suvidha deluxe`}
        canonicalUrl={`https://hotelsuvidhadeluxe.com/attractions/${attraction.slug}`}
      />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: `url(${attraction.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/about">
            <Button variant="ghost" className="text-white hover:text-gold-400 mb-6">
              <ArrowLeft className="mr-2" size={18} />
              Back to Attractions
            </Button>
          </Link>
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-gold-600 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                <MapPin size={16} className="inline mr-1" />
                {attraction.distance}
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-serif">{attraction.name}</h1>
            <p className="text-xl text-gray-300">{attraction.description}</p>
          </div>
        </div>
      </section>

      {/* Attraction Content */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="relative h-96 overflow-hidden">
            <img
              src={attraction.image}
              alt={attraction.name}
              loading="lazy"
              width={1200}
              height={400}
              className="w-full h-full object-cover"
            />
            </div>
            <div className="p-8 md:p-12">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 bg-gradient-to-r from-navy-900 to-navy-800 rounded-lg p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4 font-serif">Ready to Explore Haridwar?</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Book your stay at Hotel Suvidha Deluxe and explore all the amazing attractions Haridwar has to offer.
            </p>
            <Link to="/booking">
              <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-8 py-6 text-lg">
                Book Your Stay Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AttractionDetail;
